// Shell/Topbar (Figma 18:51) — 56px, breadcrumb left, actions + account menu
// right. PRD 18 US-011.
//
// The account menu is a <details>/<summary> on purpose: it is keyboard operable
// and Escape-dismissable with no JS, and no focus trap of our own to get wrong.

import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { useMe } from "../screens/overview/api";
import { adminHrefs } from "./hrefs";

export type TopbarProps = {
  /** Sidebar-matching identity: an org slug, or the operator's email. */
  identity: string;
  /** Trailing breadcrumb segment — the screen's own name. */
  title?: string;
  /** Page-level actions rendered left of the account menu. */
  actions?: ReactNode;
  onSignOut?: () => void;
};

export function Topbar({ identity, title, actions, onSignOut }: TopbarProps) {
  // Read directly rather than threading a prop through three call sites: this
  // is the `["auth-me"]` query the shell gate already awaits on every render,
  // so it costs no round-trip. It is also the ONLY thing that reveals the
  // operator surface — a non-staff session never renders the row, and the
  // routes answer 403 regardless.
  const me = useMe();
  const isStaff = me.data?.is_staff === true;

  return (
    <header className="maisight-shell-topbar">
      <nav className="maisight-shell-breadcrumb type-label" aria-label="Breadcrumb">
        {title ? (
          <span className="maisight-shell-breadcrumb-current" aria-current="page">
            {title}
          </span>
        ) : null}
      </nav>
      <div className="maisight-shell-topbar-actions">
        {actions}
        <details className="maisight-shell-account-menu">
          <summary
            className="maisight-shell-account-trigger type-label"
            aria-label={`Account menu for ${identity}`}
          >
            {identity}
          </summary>
          <div className="maisight-shell-account-panel">
            {isStaff ? (
              <Link
                to={adminHrefs().home}
                className="maisight-shell-account-action type-label"
                data-testid="operator-console-link"
              >
                Operator console
              </Link>
            ) : null}
            <button
              type="button"
              className="maisight-shell-account-action type-label"
              onClick={onSignOut}
            >
              Sign out
            </button>
          </div>
        </details>
      </div>
    </header>
  );
}
